import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import YhTable, { YhTableColumn, YhTableCellParams } from "../../../../ui/YhTable";
import YhInput from "../../../../ui/YhInput";
import {
  isTruthy,
  openErrorNotification,
  openSuccessNotification,
} from "../../../../helpers/methods";
import { changeFormStatusApi, getAllUsers } from "./ShowAllService";
import notifiers from "../../../../global/constants/NotificationConstants";
import CustomLoader from "../../../../global/components/CustomLoader/CustomLoader";
import { store } from "../../../../utils/store";
import { ReactComponent as VerticalDotIcon } from "../../../../assets/icons/verticalDot.svg";
import { applyLicenseKey } from "../../../../utils/License";
import customErrorHandler from "../../../../utils/customErrorHandler";
import urls from "../../../../global/constants/UrlConstants";
import {
  OrgNameCell,
  useOrgNameMap,
} from "../../../../Shared/Organization/OrganizationSelect";
import { selectActiveOrganizationId } from "../../../../redux/authSlice";
import { getFeedbackFormCounts } from "../../../Feedback/FeedbackService";
import { getRegistrationFormCounts } from "../../../Registrations/RegistrationService";
import { buildPublicFormUrl } from "../../feedbackPublicUrl";
import FormWebhookPopup from "../FormWebhookPopup";
import FormShareLinksPopup, { FormShareIconBar } from "../FormShareLinks";

const SearchSvg = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="11" cy="11" r="8" />
    <path d="M21 21l-4.35-4.35" />
  </svg>
);

const EditSvg = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const ShowAll = () => {
  useEffect(() => {
    applyLicenseKey();
  }, []);

  const privileges = store.getState().auth?.privileges || {};
  const history = useHistory();
  const orgNameMap = useOrgNameMap();
  const activeOrganizationId = useSelector(selectActiveOrganizationId) || "";
  const [userDataSource1, setUserDataSource1] = useState<any[]>([]);
  const [search, setSearch] = useState("");
  const [apiResponse, setApiResponse] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [userRow_Id, setUserRow_Id] = useState<string>();
  const [editRowData, setEditRowData] = useState<any>();
  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 25,
    page: 0,
  });
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [metaData, setMetaData] = useState<any>({});
  const [feedbackCounts, setFeedbackCounts] = useState<Record<string, number>>(
    {}
  );
  const [registrationCounts, setRegistrationCounts] = useState<
    Record<string, number>
  >({});
  const editPopoverRef = useRef<HTMLDivElement | null>(null);
  const [webhookForm, setWebhookForm] = useState<any>(null);
  const [shareForm, setShareForm] = useState<any>(null);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number }>({
    top: 0,
    left: 0,
  });

  useEffect(() => {
    setPaginationModel((p) => (p.page === 0 ? p : { ...p, page: 0 }));
    tableDataShowHandler();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, paginationModel.page, paginationModel.pageSize, activeOrganizationId]);

  useLayoutEffect(() => {
    if (!anchorEl) return;
    const MENU_W = 240;
    const MENU_H = 180;
    const rect = anchorEl.getBoundingClientRect();
    const pad = 8;
    let top = rect.bottom + 8;
    let left = rect.right - MENU_W;
    if (top + MENU_H > window.innerHeight - pad) {
      top = Math.max(pad, rect.top - MENU_H - 8);
    }
    if (left < pad) left = pad;
    if (left + MENU_W > window.innerWidth - pad) {
      left = Math.max(pad, window.innerWidth - MENU_W - pad);
    }
    setMenuPos({ top, left });
  }, [anchorEl]);

  const applyFormCounts = (fb: any, reg: any) => {
    setFeedbackCounts(
      fb?.data && typeof fb.data === "object" && !Array.isArray(fb.data)
        ? fb.data
        : {}
    );
    setRegistrationCounts(
      reg?.data && typeof reg.data === "object" && !Array.isArray(reg.data)
        ? reg.data
        : {}
    );
  };

  const handleSearch = (newValue: string) => {
    setSearch(newValue);
    setPaginationModel((p) => ({ ...p, page: 0 }));
  };

  const onClickHandler = () => {
    history.push(urls.ADDLEADFORMPAGE);
  };

  const handleEdit = (_id: string) => {
    const row =
      editRowData ||
      userDataSource1?.find((r: any) => r._id === _id);
    if (row) {
      sessionStorage.setItem(`lead-form-edit-${_id}`, JSON.stringify(row));
    }
    setAnchorEl(null);
    history.push(urls.EDITLEADFORMPAGE.replace(":id", _id));
  };

  const handlePopOver = (
    event: React.MouseEvent<HTMLButtonElement>,
    user_id: string,
    userData: any
  ) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setUserRow_Id(user_id);
    setEditRowData(userData);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const changeStatus = async (status: any, id: any) => {
    try {
      const response = await changeFormStatusApi(status, id);
      customErrorHandler(response);
      openSuccessNotification(response.message);
      tableDataShowHandler();
    } catch (error: any) {
      openErrorNotification(
        isTruthy(error.message) ? error.message : notifiers.GENERIC_ERROR
      );
    }
  };

  const columns: YhTableColumn[] = [
    {
      headerName: "Form",
      field: "form_name",
      minWidth: 240,
      flex: 1,
      renderCell: (params: YhTableCellParams) => {
        const name = params.row?.form_name || "?";
        const initial = String(name).trim().charAt(0).toUpperCase() || "?";
        const avatarPalette = [
          { bg: "#F9F0F4", color: "#6B1630" },
          { bg: "#DBEAFE", color: "#2563EB" },
          { bg: "#D1FAE5", color: "#059669" },
          { bg: "#FFEDD5", color: "#EA580C" },
          { bg: "#FEF3C7", color: "#D97706" },
          { bg: "#FCE7F3", color: "#DB2777" },
          { bg: "#CCFBF1", color: "#0D9488" },
        ];
        let hash = 0;
        for (let i = 0; i < String(name).length; i++) {
          hash = String(name).charCodeAt(i) + ((hash << 5) - hash);
        }
        const avatar = avatarPalette[Math.abs(hash) % avatarPalette.length];

        return (
          <div className="flex items-center gap-3">
            <span
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl text-sm font-bold"
              style={{ backgroundColor: avatar.bg, color: avatar.color }}
            >
              {initial}
            </span>
            <div className="min-w-0 flex flex-col items-start gap-1">
              <span className="truncate text-sm font-semibold capitalize text-[#111827]">
                {name}
              </span>
              <button
                type="button"
                data-action-button="true"
                className="action-button inline-flex items-center rounded-lg bg-[#F9F0F4] px-2 py-0.5 text-[11px] font-semibold text-[#841C39] hover:bg-[#F9F0F4]"
                onClick={(e) => {
                  e.stopPropagation();
                  setWebhookForm(params.row);
                }}
              >
                Webhook
              </button>
            </div>
          </div>
        );
      },
    },
    {
      headerName: "Type",
      field: "formType",
      minWidth: 140,
      renderCell: (params: YhTableCellParams) => {
        const type = params.row?.formType || "normal";
        const isPublic = type === "feedback" || type === "registration";
        const scope =
          params.row?.formScope ||
          (params.row?.projectSlug ? "project" : "organization");
        const label =
          type === "feedback"
            ? "Feedback"
            : type === "registration"
              ? "Registration"
              : type === "website"
                ? "Website"
                : "Normal";
        const badgeClass =
          type === "feedback"
            ? "bg-sky-50 text-sky-700"
            : type === "registration"
              ? "bg-amber-50 text-amber-800"
              : type === "website"
                ? "bg-[#F9F0F4] text-[#841C39]"
                : "bg-slate-100 text-slate-600";
        return (
          <div className="flex flex-col items-start gap-0.5">
            <span
              className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${badgeClass}`}
            >
              {label}
            </span>
            {isPublic ? (
              <span className="text-[11px] capitalize text-[#6B7280]">
                {scope} level
              </span>
            ) : type === "website" ? (
              <span className="text-[11px] text-[#6B7280]">
                All Yhataw projects
              </span>
            ) : null}
          </div>
        );
      },
    },
    {
      headerName: "Responses",
      field: "feedbackCount",
      minWidth: 110,
      renderCell: (params: YhTableCellParams) => {
        const type = params.row?.formType;
        if (type !== "feedback" && type !== "registration") {
          return <span className="text-sm text-[#9CA3AF]">—</span>;
        }
        const formId = String(params.row?._id || "");
        const count =
          type === "registration"
            ? Number(registrationCounts[formId] || 0)
            : Number(feedbackCounts[formId] || 0);
        const listPath =
          type === "registration" ? urls.REGISTRATIONSLIST : urls.FEEDBACKLIST;
        return (
          <button
            type="button"
            className="inline-flex items-center gap-1 rounded-full bg-[#F9F0F4] px-2.5 py-1 text-xs font-bold text-[#841C39] hover:bg-[#F9F0F4]"
            onClick={(e) => {
              e.stopPropagation();
              history.push(
                formId
                  ? `${listPath}?formId=${encodeURIComponent(formId)}`
                  : listPath
              );
            }}
          >
            {count}
          </button>
        );
      },
    },
    {
      headerName: "Copy links",
      field: "publicUrl",
      minWidth: 250,
      renderCell: (params: YhTableCellParams) => {
        if (
          params.row?.formType !== "feedback" &&
          params.row?.formType !== "registration"
        ) {
          return <span className="text-sm text-[#9CA3AF]">—</span>;
        }
        const link = buildPublicFormUrl(params.row);
        if (!link) {
          return <span className="text-sm text-[#9CA3AF]">—</span>;
        }
        return (
          <FormShareIconBar
            form={params.row}
            onOpenAll={() => setShareForm(params.row)}
          />
        );
      },
    },
    {
      headerName: "Property",
      field: "project_name",
      minWidth: 180,
      renderCell: (params: YhTableCellParams) => (
        <div className="flex min-w-0 flex-col items-start gap-0.5">
          <span className="truncate text-sm font-semibold capitalize text-[#111827]">
            {params.row?.formType === "website"
              ? "All Yhataw projects"
              : params.row?.project_name || "—"}
          </span>
          <span className="truncate text-xs capitalize text-[#6B7280]">
            {params.row?.developer_name || ""}
          </span>
        </div>
      ),
    },
    {
      headerName: "Organization",
      field: "organizationId",
      minWidth: 150,
      renderCell: (params: YhTableCellParams) => (
        <OrgNameCell
          organizationId={params.row?.organizationId}
          map={orgNameMap}
          fallbackLabel={params.row?.organizationName}
        />
      ),
    },
    {
      headerName: "Property Type",
      field: "projecttype_name",
      minWidth: 150,
      renderCell: (params: YhTableCellParams) => {
        const unitConfig = params.row?.unitConfig;
        const fromUnit = Array.isArray(unitConfig)
          ? unitConfig
              .map((u: any) => u?.name || u?.label || u?.type || (typeof u === "string" ? u : ""))
              .filter(Boolean)
              .join(", ")
          : typeof unitConfig === "string"
          ? unitConfig
          : "";
        const type =
          params.row?.projecttype_name ||
          params.row?.propertyType ||
          fromUnit ||
          "—";

        return (
          <span className="truncate text-sm capitalize text-[#374151]">
            {type}
          </span>
        );
      },
    },
    {
      headerName: "Leads",
      field: "leadName",
      minWidth: 200,
      renderCell: (params: YhTableCellParams) => (
        <div className="flex min-w-0 flex-col items-start gap-0.5">
          <span className="truncate text-sm font-semibold capitalize text-[#111827]">
            {params.row?.leadName || "—"}
          </span>
          <span className="truncate text-xs text-[#6B7280]">
            {params.row?.leadEmail || ""}
          </span>
          <span className="truncate text-xs text-[#9CA3AF]">
            {params.row?.leadPhone
              ? `${params.row?.phoneCountryCode || ""} ${params.row.leadPhone}`.trim()
              : ""}
          </span>
        </div>
      ),
    },
    {
      headerName: "Status",
      field: "status",
      minWidth: 120,
      renderCell: (params: YhTableCellParams) => {
        const isActive = String(params.row?.status) === "1" || params.row?.status === 1;
        const isWebsite = params.row?.formType === "website";
        const canToggle =
          !isWebsite &&
          (privileges?.["Lead Form"]?.includes("delete") ||
            privileges?.["Lead Form"]?.includes("full control"));

        if (!canToggle) {
          return (
            <span
              className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
                isActive
                  ? "bg-emerald-50 text-emerald-700"
                  : "bg-red-50 text-red-600"
              }`}
            >
              {isActive ? "Active" : "Deactive"}
            </span>
          );
        }

        return (
          <button
            type="button"
            data-action-button="true"
            className={`action-button inline-flex rounded-full px-2.5 py-1 text-xs font-semibold transition ${
              isActive
                ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                : "bg-red-50 text-red-600 hover:bg-red-100"
            }`}
            onClick={(e) => {
              e.stopPropagation();
              changeStatus(params.row.status, params.row?._id);
            }}
          >
            {isActive ? "Active" : "Deactive"}
          </button>
        );
      },
    },
    {
      headerName: "Action",
      field: "action",
      minWidth: 80,
      renderCell: (params: YhTableCellParams) => (
        <button
          type="button"
          data-action-button="true"
          className="action-button flex h-8 w-8 items-center justify-center rounded-yh-md text-yh-black hover:bg-yh-lilac"
          onClick={(event: any) =>
            handlePopOver(event, params.row._id, params.row)
          }
        >
          <VerticalDotIcon />
        </button>
      ),
    },
  ];

  const tableDataShowHandler = async () => {
    try {
      setApiResponse(true);
      setLoading(true);
      const orgId =
        activeOrganizationId &&
        activeOrganizationId !== "all" &&
        activeOrganizationId !== "*"
          ? String(activeOrganizationId)
          : "";
      const payload = {
        pageNo: paginationModel.page,
        pageSize: paginationModel.pageSize,
        search: search || "",
        organizationId: orgId,
      };
      const [response, fb, reg] = await Promise.all([
        getAllUsers(payload),
        getFeedbackFormCounts().catch(() => null),
        getRegistrationFormCounts().catch(() => null),
      ]);
      setMetaData(response?.metadata?.[0] ? response.metadata[0] : {});
      setUserDataSource1(response?.data || []);
      applyFormCounts(fb, reg);
    } catch (error: any) {
      openErrorNotification(
        isTruthy(error.message) ? error.message : notifiers.GENERIC_ERROR
      );
    } finally {
      setApiResponse(false);
      setLoading(false);
    }
  };

  const canView =
    privileges?.["Lead Form"]?.includes("view") ||
    privileges?.["Lead Form"]?.includes("full control");
  const canCreate =
    privileges?.["Lead Form"]?.includes("create") ||
    privileges?.["Lead Form"]?.includes("full control");
  const canEdit =
    privileges?.["Lead Form"]?.includes("edit") ||
    privileges?.["Lead Form"]?.includes("full control");

  return (
    <div className="flex h-full min-h-0 w-full flex-col">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-4 shrink-0">
        <div>
          <h3 className="text-xl font-bold tracking-tight text-[#111827]">
            All Forms
          </h3>
          <p className="mt-1 text-sm text-[#6B7280]">
            View &amp; manage forms for the organization selected in the header.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="min-w-[440px] w-full sm:w-[320px]">
            <YhInput
              placeholder="Search forms…"
              value={search}
              type="search"
              onChange={(e) => handleSearch(e.target.value)}
              autoFocus={isSearchFocused}
              startAdornment={<SearchSvg />}
              onFocus={() => setIsSearchFocused(true)}
              onBlur={() => setIsSearchFocused(false)}
              containerClassName="w-full"
            />
          </div>

          {canCreate ? (
            <button
              type="button"
              onClick={() => onClickHandler()}
              className="inline-flex h-10 shrink-0 items-center gap-1 rounded-xl bg-[#A02448] px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-[#841C39]"
            >
              + New Form
            </button>
          ) : null}
        </div>
      </div>

      <div className="relative min-h-0 flex-1">
        {canView ? (
          <YhTable
            columns={columns}
            rows={userDataSource1}
            getRowId={(row) => row._id}
            rowHeight={76}
            loading={loading}
            emptyMessage="No data found"
            className="h-full min-h-[280px]"
            page={paginationModel.page}
            pageSize={paginationModel.pageSize}
            totalRows={metaData?.total || 0}
            onPageChange={(p) =>
              setPaginationModel((prev) => ({ ...prev, page: p }))
            }
            onPageSizeChange={(s) =>
              setPaginationModel({ pageSize: s, page: 0 })
            }
          />
        ) : null}

        {anchorEl
          ? createPortal(
              <>
                <div
                  className="fixed inset-0 z-[9998]"
                  onClick={handleClose}
                />
                <div
                  ref={editPopoverRef}
                  className="fixed z-[9999] flex w-[240px] max-w-[calc(100vw-16px)] flex-col items-start rounded-yh-md border border-yh-border bg-white p-1 shadow-yh"
                  style={{ top: menuPos.top, left: menuPos.left }}
                >
                  {canEdit ? (
                    <button
                      type="button"
                      className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-[#111827] hover:bg-[#F8FAFC]"
                      onClick={() => handleEdit(userRow_Id ?? "")}
                    >
                      <EditSvg />
                      Edit
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-[#111827] hover:bg-[#F8FAFC]"
                    onClick={() => {
                      setWebhookForm(editRowData);
                      handleClose();
                    }}
                  >
                    Webhook URL
                  </button>
                  {(editRowData?.formType === "feedback" ||
                    editRowData?.formType === "registration") &&
                  buildPublicFormUrl(editRowData) ? (
                    <button
                      type="button"
                      className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-[#111827] hover:bg-[#F8FAFC]"
                      onClick={() => {
                        setShareForm(editRowData);
                        handleClose();
                      }}
                    >
                      Copy public / platform links
                    </button>
                  ) : null}
                </div>
              </>,
              document.body
            )
          : null}
      </div>

      <FormWebhookPopup
        open={!!webhookForm}
        form={webhookForm}
        onClose={() => setWebhookForm(null)}
      />
      <FormShareLinksPopup
        open={!!shareForm}
        form={shareForm}
        onClose={() => setShareForm(null)}
      />

      {apiResponse && <CustomLoader apiResponse={false} />}
    </div>
  );
};

export default ShowAll;
